/**
 * VaultFS over the GitHub REST API.
 *
 * This is the production adapter: your phone and your desktop both talk to the
 * same private repo, and git handles the sync that would otherwise need a
 * server.
 *
 * Three details drive the design:
 *
 *  - **One request to list, not N.** The Git Trees API returns every path and
 *    blob sha in a single recursive call. Walking the Contents API directory by
 *    directory would be dozens of round trips before the first pixel.
 *
 *  - **Blob shas are free cache keys.** GitHub's sha is the content hash, so an
 *    unchanged file can be served from cache without refetching. That's what
 *    makes reopening the app cheap on a phone.
 *
 *  - **Conflicts are expected, not exceptional.** Every write carries the blob
 *    sha it was based on; GitHub rejects a stale one with 409, which the store
 *    turns into a re-read and an intent replay. Two devices editing the same
 *    file is a normal Tuesday.
 */

import {
  VaultConflictError,
  VaultNotFoundError,
  type VaultEntry,
  type VaultFile,
  type VaultFS,
} from '@hud/core';

export interface GitHubVaultOptions {
  owner: string;
  repo: string;
  /** Fine-grained PAT with contents read/write on this repo only. */
  token: string;
  branch?: string;
  fetchImpl?: typeof fetch;
  /** Commit message for writes. Defaults to a generic one. */
  commitMessage?: (path: string) => string;
  /** Author shown on commits made from the HUD. */
  committer?: { name: string; email: string };
}

interface TreeResponse {
  tree: { path: string; type: string; sha: string; size?: number }[];
  truncated: boolean;
}

const API = 'https://api.github.com';

export class GitHubVaultFS implements VaultFS {
  readonly #options: Required<Pick<GitHubVaultOptions, 'owner' | 'repo' | 'branch' | 'token'>> &
    GitHubVaultOptions;
  readonly #fetch: typeof fetch;
  /** blob sha → decoded content. Shas are content hashes, so entries never go stale. */
  readonly #blobCache = new Map<string, string>();
  /** Latest known sha per path, so writes can send a base without a re-read. */
  readonly #shaByPath = new Map<string, string>();

  constructor(options: GitHubVaultOptions) {
    this.#options = { branch: 'main', ...options };
    this.#fetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  get repoSlug(): string {
    return `${this.#options.owner}/${this.#options.repo}`;
  }

  async list(): Promise<VaultEntry[]> {
    const { owner, repo, branch } = this.#options;
    const res = await this.#request(
      `${API}/repos/${owner}/${repo}/git/trees/${encodeURIComponent(branch)}?recursive=1`,
    );

    if (res.status === 404) {
      throw new Error(
        `Repository ${this.repoSlug} or branch "${branch}" not found. Check the name, the branch, and that the token grants access.`,
      );
    }
    await this.#assertOk(res, 'list vault');

    const body = (await res.json()) as TreeResponse;
    if (body.truncated) {
      // Past ~100k entries GitHub silently drops the tail. Better to say so than
      // to render a dashboard that's quietly missing files.
      console.warn('[hud] vault tree truncated by GitHub — some files are not visible');
    }

    const entries = body.tree
      .filter((node) => node.type === 'blob')
      .map((node) => ({ path: node.path, sha: node.sha, size: node.size ?? 0 }));

    for (const entry of entries) this.#shaByPath.set(entry.path, entry.sha);
    return entries;
  }

  async read(path: string): Promise<VaultFile> {
    const knownSha = this.#shaByPath.get(path);
    if (knownSha) {
      const cached = this.#blobCache.get(knownSha);
      if (cached !== undefined) return { path, content: cached, sha: knownSha };
    }

    const { owner, repo, branch } = this.#options;
    const res = await this.#request(
      `${API}/repos/${owner}/${repo}/contents/${encodePath(path)}?ref=${encodeURIComponent(branch)}`,
    );

    if (res.status === 404) throw new VaultNotFoundError(path);
    await this.#assertOk(res, `read ${path}`);

    const body = (await res.json()) as { content?: string; sha: string; encoding?: string };
    const content = body.content ? decodeBase64(body.content) : '';

    this.#shaByPath.set(path, body.sha);
    this.#blobCache.set(body.sha, content);
    return { path, content, sha: body.sha };
  }

  async write(path: string, content: string, baseSha?: string): Promise<{ sha: string }> {
    const { owner, repo, branch, commitMessage, committer } = this.#options;
    // GitHub requires the current blob sha to replace a file, and its absence to
    // create one. Sending a stale sha is exactly how we detect a lost race.
    const sha = baseSha ?? this.#shaByPath.get(path);

    const res = await this.#request(`${API}/repos/${owner}/${repo}/contents/${encodePath(path)}`, {
      method: 'PUT',
      body: JSON.stringify({
        message: commitMessage?.(path) ?? `Update ${path} from HUD`,
        content: encodeBase64(content),
        branch,
        ...(sha ? { sha } : {}),
        ...(committer ? { committer } : {}),
      }),
    });

    // 409 is an explicit conflict; 422 is what GitHub returns when the sha is
    // missing on an existing file, which is the same situation wearing a
    // different status code.
    if (res.status === 409 || res.status === 422) {
      const actual = await this.#currentSha(path);
      if (actual !== sha) throw new VaultConflictError(path, sha, actual);
    }
    await this.#assertOk(res, `write ${path}`);

    const body = (await res.json()) as { content: { sha: string } };
    this.#shaByPath.set(path, body.content.sha);
    this.#blobCache.set(body.content.sha, content);
    return { sha: body.content.sha };
  }

  async remove(path: string, baseSha?: string): Promise<void> {
    const { owner, repo, branch, committer } = this.#options;
    const sha = baseSha ?? (await this.#currentSha(path));
    if (!sha) throw new VaultNotFoundError(path);

    const res = await this.#request(`${API}/repos/${owner}/${repo}/contents/${encodePath(path)}`, {
      method: 'DELETE',
      body: JSON.stringify({
        message: `Delete ${path} from HUD`,
        sha,
        branch,
        ...(committer ? { committer } : {}),
      }),
    });

    if (res.status === 409) throw new VaultConflictError(path, sha, await this.#currentSha(path));
    await this.#assertOk(res, `delete ${path}`);

    this.#shaByPath.delete(path);
  }

  /** URL an `<img>` can load. Blobs need the token, so this returns a data URL. */
  async assetDataUrl(path: string): Promise<string> {
    const { owner, repo, branch } = this.#options;
    const res = await this.#request(
      `${API}/repos/${owner}/${repo}/contents/${encodePath(path)}?ref=${encodeURIComponent(branch)}`,
      { headers: { accept: 'application/vnd.github.raw' } },
    );
    if (!res.ok) throw new VaultNotFoundError(path);

    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error(`Could not read ${path}`));
      reader.readAsDataURL(blob);
    });
  }

  async #currentSha(path: string): Promise<string | undefined> {
    try {
      const { sha } = await this.read(path);
      return sha;
    } catch {
      return undefined;
    }
  }

  #request(url: string, init: RequestInit = {}): Promise<Response> {
    return this.#fetch(url, {
      ...init,
      headers: {
        accept: 'application/vnd.github+json',
        authorization: `Bearer ${this.#options.token}`,
        'x-github-api-version': '2022-11-28',
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  }

  /** Turn GitHub's error shapes into something a user can act on. */
  async #assertOk(res: Response, what: string): Promise<void> {
    if (res.ok) return;

    if (res.status === 401) {
      throw new Error('GitHub rejected the token. It may be expired or revoked.');
    }
    if (res.status === 403) {
      const remaining = res.headers.get('x-ratelimit-remaining');
      if (remaining === '0') {
        const reset = res.headers.get('x-ratelimit-reset');
        const when = reset ? new Date(Number(reset) * 1000).toLocaleTimeString() : 'shortly';
        throw new Error(`GitHub rate limit reached. Resets around ${when}.`);
      }
      throw new Error(
        `Token lacks permission to ${what}. It needs Contents: read and write on ${this.repoSlug}.`,
      );
    }

    let detail = '';
    try {
      const body = (await res.json()) as { message?: string };
      detail = body.message ? ` — ${body.message}` : '';
    } catch {
      /* non-JSON error body */
    }
    throw new Error(`Could not ${what}: ${res.status}${detail}`);
  }
}

/** Each segment encoded, but slashes preserved as path separators. */
function encodePath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}

/**
 * GitHub speaks base64 over UTF-8 bytes; `btoa`/`atob` speak latin-1. Going
 * through TextEncoder is what keeps é, —, and emoji intact through a round trip.
 */
export function encodeBase64(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function decodeBase64(base64: string): string {
  // The Contents API wraps long base64 at 60 chars; atob rejects the newlines.
  const binary = atob(base64.replace(/\s/g, ''));
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
